import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Typography,
    Checkbox,
    CircularProgress,
    Tooltip,
    Button,
    Chip,
    useMediaQuery,
} from '@mui/material';
import { alpha, useTheme, SxProps, Theme } from '@mui/material/styles';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import Delete from '@mui/icons-material/Delete';
import Download from '@mui/icons-material/Download';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { AssetGridProps, Asset, FolderTreeResponse, folderDisplayName } from '../AssetExplorer.types';
import { assetSupportsInlineImagePreview, formatFileSize } from '../AssetExplorer.api';
import { useAssetExplorerApi } from '../AssetExplorerConfig';
import { assetExplorerFolderIconColor } from '../assetExplorerTheme';
import { PublishedAssetGlobeButton } from './PublishedAssetGlobeButton';
import { FolderStateIcons } from './FolderStateIcons';

/** Overlay checkbox on tiles — solid light plate so the mark stays visible in dark theme. */
const tileOverlayCheckboxSx = (theme: Theme, selected: boolean): SxProps<Theme> => ({
    position: 'absolute',
    top: 4,
    right: 4,
    zIndex: 4,
    p: 0.35,
    opacity: selected ? 1 : 0,
    transition: 'opacity 0.2s ease',
    bgcolor: '#fff',
    color: '#1a1a1a',
    borderRadius: 1,
    border: `2px solid ${alpha('#000', 0.55)}`,
    boxSizing: 'border-box',
    boxShadow: `
        inset 0 0 0 1px ${alpha('#fff', 0.9)},
        0 2px 8px ${alpha('#000', 0.35)}
    `,
    '& .MuiSvgIcon-root': {
        color: '#1a1a1a',
    },
    '&:hover': {
        bgcolor: '#fff',
        borderColor: alpha('#000', 0.72),
    },
    '&.Mui-checked': {
        opacity: 1,
        bgcolor: '#fff',
        borderColor: theme.palette.secondary.main,
        color: '#141937',
        '& .MuiSvgIcon-root': {
            color: '#141937',
        },
    },
    '&.Mui-checked:hover': {
        bgcolor: '#fff',
        borderColor: theme.palette.secondary.dark,
    },
});

/** Placeholder icon: VIDEO / AUDIO keep distinct glyphs; IMAGE without preview (e.g. HEIC) and all other types use one generic file icon */
const getAssetHeroIcon = (type: string, size: number = 40) => {
    const iconProps = { sx: { fontSize: size, color: 'inherit' } };

    switch (type) {
        case 'VIDEO':
            return <VideoFileIcon {...iconProps} />;
        case 'AUDIO':
            return <AudioFileIcon {...iconProps} />;
        default:
            return <InsertDriveFileIcon {...iconProps} />;
    }
};

interface AssetTileProps {
    asset: Asset;
    isSelected: boolean;
    onSelect: (assetId: string) => void;
    onThumbnailClick: (asset: Asset) => void;
    onDownload: (asset: Asset) => void;
    onDelete: (asset: Asset) => void;
    readOnly?: boolean;
}

const AssetTile: React.FC<AssetTileProps> = ({
    asset,
    isSelected,
    onSelect,
    onThumbnailClick,
    onDownload,
    onDelete,
    readOnly = false,
}) => {
    const theme = useTheme();
    const supportsHoverPlay = useMediaQuery('(hover: hover)');
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const [imgBroken, setImgBroken] = useState(false);
    const [tileHoveredForVideo, setTileHoveredForVideo] = useState(false);

    const showInlineVideo = asset.type === 'VIDEO' && Boolean(asset.s3Url);

    useEffect(() => {
        setImgBroken(false);
        setTileHoveredForVideo(false);
    }, [asset._id, asset.s3Url]);

    useEffect(() => {
        const v = videoRef.current;
        if (!v || !showInlineVideo) return;

        if (supportsHoverPlay && tileHoveredForVideo) {
            v.muted = true;
            v.play().catch(() => undefined);
        } else {
            v.pause();
            try {
                v.currentTime = 0;
            } catch {
                /* ignore */
            }
        }
    }, [showInlineVideo, supportsHoverPlay, tileHoveredForVideo]);

    const showInlineImage =
        asset.type === 'IMAGE' && Boolean(asset.s3Url) && assetSupportsInlineImagePreview(asset) && !imgBroken;

    const isPublished = Boolean(asset.publishedRelativeUrl && String(asset.publishedRelativeUrl).trim());

    const handleTileMouseEnter = () => {
        if (showInlineVideo && supportsHoverPlay) {
            setTileHoveredForVideo(true);
        }
    };

    const handleTileMouseLeave = () => {
        if (showInlineVideo && supportsHoverPlay) {
            setTileHoveredForVideo(false);
        }
    };

    return (
        <Box
            className="asset-tile-root"
            onMouseEnter={handleTileMouseEnter}
            onMouseLeave={handleTileMouseLeave}
            sx={{
                width: 260,
                height: 200,
                borderRadius: 2.5,
                overflow: 'hidden',
                position: 'relative',
                boxShadow: isPublished
                    ? `0 2px 10px rgba(0,0,0,0.12), 0 0 0 2px ${alpha(theme.palette.success.main, 0.65)}, 0 10px 32px ${alpha(theme.palette.success.main, 0.38)}`
                    : '0 2px 8px rgba(0,0,0,0.15)',
                flexShrink: 0,
                border: isSelected ? `2px solid ${theme.palette.secondary.main}` : '1px solid',
                borderColor: isSelected ? undefined : 'divider',
                '&:hover .tile-overlay': { height: 120 },
                '&:hover .tile-details': { opacity: 1 },
                '&:hover .tile-name-short': { opacity: 0, height: 0, lineHeight: 0 },
                '&:hover .tile-select-checkbox': { opacity: 1 },
                '@media (hover: none)': {
                    '& .tile-overlay': { height: 120 },
                    '& .tile-details': { opacity: 1 },
                    '& .tile-name-short': { opacity: 0, height: 0, lineHeight: 0 },
                    '& .tile-select-checkbox': { opacity: 1 },
                },
            }}
        >
            {/* Thumbnail / hero — opens carousel */}
            <Box
                onClick={() => onThumbnailClick(asset)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onThumbnailClick(asset);
                    }
                }}
                role="button"
                tabIndex={0}
                aria-label={`Open preview for ${asset.name}`}
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 40,
                    zIndex: 1,
                    cursor: 'pointer',
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.5,
                    overflow: 'hidden',
                }}
            >
                {showInlineImage ? (
                    <Box
                        component="img"
                        src={asset.s3Url}
                        alt={asset.name}
                        sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            position: 'absolute',
                            inset: 0,
                        }}
                        onError={() => setImgBroken(true)}
                    />
                ) : showInlineVideo ? (
                    <>
                        <Box
                            component="video"
                            ref={videoRef}
                            src={asset.s3Url}
                            muted
                            playsInline
                            loop
                            preload="metadata"
                            sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                position: 'absolute',
                                inset: 0,
                                display: 'block',
                                bgcolor: '#000',
                            }}
                        />
                        <Box
                            aria-hidden
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'rgba(0,0,0,0.28)',
                                opacity: supportsHoverPlay && tileHoveredForVideo ? 0 : 1,
                                transition: 'opacity 0.22s ease',
                                pointerEvents: 'none',
                                zIndex: 2,
                            }}
                        >
                            <PlayCircleOutlineIcon
                                sx={{
                                    fontSize: 56,
                                    color: '#fff',
                                    opacity: 0.96,
                                    filter: 'drop-shadow(0 2px 10px rgba(0,0,0,0.55))',
                                }}
                            />
                        </Box>
                    </>
                ) : (
                    <Box sx={{ color: 'rgba(255,255,255,0.9)', zIndex: 1 }}>{getAssetHeroIcon(asset.type, 44)}</Box>
                )}
            </Box>

            <Box
                sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    zIndex: 4,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    pointerEvents: 'none',
                }}
            >
                <Chip
                    label={asset.type}
                    size="small"
                    sx={{
                        height: 22,
                        fontSize: '0.68rem',
                        fontWeight: 700,
                        color: '#fff',
                        backgroundColor: 'rgba(0,0,0,0.45)',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                        pointerEvents: 'none',
                    }}
                />
                {isPublished && (
                    <PublishedAssetGlobeButton
                        className="tile-publish-globe"
                        publishedRelativeUrl={asset.publishedRelativeUrl}
                        sx={{
                            pointerEvents: 'auto',
                            p: 0.2,
                            width: 22,
                            height: 22,
                            bgcolor: 'rgba(255,255,255,0.94)',
                            borderRadius: 1,
                            border: `2px solid ${alpha(theme.palette.common.black, 0.48)}`,
                            boxSizing: 'border-box',
                            boxShadow: `
                                inset 0 0 0 1px ${alpha(theme.palette.common.white, 0.85)},
                                0 2px 8px ${alpha(theme.palette.common.black, 0.28)}
                            `,
                            color: theme.palette.success.dark,
                            '& .MuiSvgIcon-root': { fontSize: 16 },
                            '&:hover': {
                                bgcolor: '#fff',
                                borderColor: alpha(theme.palette.common.black, 0.62),
                            },
                        }}
                    />
                )}
            </Box>

            {/* Bulk-select checkbox — top right on hover (always visible when touch / selected) */}
            <Checkbox
                className="tile-select-checkbox"
                checked={isSelected}
                size="small"
                onChange={(e) => {
                    e.stopPropagation();
                    onSelect(asset._id);
                }}
                onClick={(e) => e.stopPropagation()}
                sx={tileOverlayCheckboxSx(theme, isSelected)}
            />

            <Box
                className="tile-overlay"
                sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    overflow: 'hidden',
                    background:
                        'linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.72) 50%, rgba(0,0,0,0.25) 100%)',
                    transition: 'height 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                    height: 40,
                    px: 1.5,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    zIndex: 3,
                }}
            >
                <Typography
                    variant="subtitle2"
                    noWrap
                    className="tile-name-short"
                    sx={{
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: '0.82rem',
                        lineHeight: '40px',
                        textShadow: '0 1px 3px rgba(0,0,0,0.4)',
                        flexShrink: 0,
                        transition: 'opacity 0.2s ease, height 0.2s ease, line-height 0.2s ease',
                        pointerEvents: 'none',
                    }}
                >
                    {asset.name}
                </Typography>

                <Box
                    className="tile-details"
                    sx={{
                        opacity: 0,
                        transition: 'opacity 0.3s ease 0.08s',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                        pt: 0.25,
                        pb: 1,
                        flex: 1,
                    }}
                >
                    <Typography
                        variant="subtitle2"
                        sx={{
                            color: '#fff',
                            fontWeight: 700,
                            fontSize: '0.78rem',
                            lineHeight: 1.3,
                            textShadow: '0 1px 3px rgba(0,0,0,0.4)',
                            wordBreak: 'break-word',
                        }}
                    >
                        {asset.name}
                    </Typography>
                    <Typography
                        variant="caption"
                        sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.68rem', lineHeight: 1.2 }}
                    >
                        {formatFileSize(asset.size)}
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mt: 'auto',
                            gap: 0.5,
                        }}
                    >
                        <Tooltip title="Download">
                            <Button
                                color="secondary"
                                variant="contained"
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDownload(asset);
                                }}
                                sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
                            >
                                <Download fontSize="small" />
                            </Button>
                        </Tooltip>
                        {!readOnly && (
                        <Tooltip title="Delete">
                            <Button
                                color="error"
                                variant="contained"
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDelete(asset);
                                }}
                                sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
                            >
                                <Delete fontSize="small" />
                            </Button>
                        </Tooltip>
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

interface FolderTileProps {
    folder: FolderTreeResponse;
    onOpen: (folder: FolderTreeResponse) => void;
    isSelected: boolean;
    onToggleSelect: (folderId: string) => void;
}

const folderThumbCache = new Map<string, string>();

const FolderTile: React.FC<FolderTileProps> = ({ folder, onOpen, isSelected, onToggleSelect }) => {
    const theme = useTheme();
    const displayName = folderDisplayName(folder);
    const { fetchFolderThumbnailAsDataUrl, basePath: apiBasePath } = useAssetExplorerApi();
    const cacheKey = `${apiBasePath}:${folder._id}:${folder.thumbnailUpdatedAt || ''}`;
    const [thumbUrl, setThumbUrl] = useState<string | null>(
        folder.hasThumbnail ? folderThumbCache.get(cacheKey) || null : null,
    );

    useEffect(() => {
        if (!folder.hasThumbnail) {
            setThumbUrl(null);
            return;
        }
        const cached = folderThumbCache.get(cacheKey);
        if (cached) {
            setThumbUrl(cached);
            return;
        }
        let cancelled = false;
        fetchFolderThumbnailAsDataUrl(folder._id, folder.thumbnailUpdatedAt).then((url) => {
            if (!url || cancelled) return;
            folderThumbCache.set(cacheKey, url);
            setThumbUrl(url);
        });
        return () => {
            cancelled = true;
        };
    }, [cacheKey, folder._id, folder.hasThumbnail, folder.thumbnailUpdatedAt, fetchFolderThumbnailAsDataUrl]);

    return (
        <Box
            sx={{
                width: 260,
                height: 200,
                borderRadius: 2.5,
                flexShrink: 0,
                position: 'relative',
                border: isSelected
                    ? `1px solid ${theme.palette.secondary.main}`
                    : `1px solid ${theme.palette.divider}`,
                background: `linear-gradient(155deg, ${alpha(theme.palette.secondary.main, 0.16)} 0%, ${alpha(theme.palette.secondary.dark, 0.06)} 100%)`,
                transition: 'border-color 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease',
                '&:hover': {
                    borderColor: theme.palette.secondary.main,
                    boxShadow: `0 6px 18px ${alpha(theme.palette.secondary.main, 0.22)}`,
                    transform: 'translateY(-2px)',
                    '& .folder-tile-select-checkbox': { opacity: 1 },
                },
                '@media (hover: none)': {
                    '& .folder-tile-select-checkbox': { opacity: 1 },
                },
            }}
        >
            {!folder.shared && (
            <Checkbox
                className="folder-tile-select-checkbox"
                checked={isSelected}
                size="small"
                onChange={(e) => {
                    e.stopPropagation();
                    onToggleSelect(folder._id);
                }}
                onClick={(e) => e.stopPropagation()}
                sx={tileOverlayCheckboxSx(theme, isSelected)}
            />
            )}

            {(folder.published || folder.sharedWithCompany || folder.shared) && (
                <Box sx={{ position: 'absolute', top: 8, left: 8, zIndex: 4 }} onClick={(e) => e.stopPropagation()}>
                    <FolderStateIcons folder={folder} />
                </Box>
            )}

            <Box
                role="button"
                tabIndex={0}
                onClick={() => onOpen(folder)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onOpen(folder);
                    }
                }}
                aria-label={`Open folder ${displayName}`}
                sx={{
                    cursor: 'pointer',
                    height: '100%',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    px: 2,
                    borderRadius: 2.5,
                    overflow: 'hidden',
                    '&:focus-visible': {
                        outline: `2px solid ${theme.palette.secondary.main}`,
                        outlineOffset: 2,
                    },
                }}
            >
                {thumbUrl ? (
                    <Box
                        component="img"
                        src={thumbUrl}
                        alt=""
                        onError={() => setThumbUrl(null)}
                        sx={{
                            width: 96,
                            height: 96,
                            objectFit: 'cover',
                            borderRadius: 1.5,
                            flexShrink: 0,
                        }}
                    />
                ) : (
                    <FolderOutlinedIcon sx={{ fontSize: 72, color: assetExplorerFolderIconColor(theme) }} />
                )}
                <Typography
                    variant="subtitle1"
                    sx={{
                        fontWeight: 700,
                        textAlign: 'center',
                        color:
                            theme.palette.mode === 'light'
                                ? alpha(theme.palette.grey[900], 0.94)
                                : theme.palette.secondary.dark,
                    }}
                    noWrap
                >
                    {displayName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    Folder
                </Typography>
            </Box>
        </Box>
    );
};

export const AssetGrid: React.FC<AssetGridProps> = ({
    childFolders = [],
    onFolderOpen,
    selectedFolderIds = new Set(),
    onFolderToggleSelect,
    assets,
    selectedAssets,
    onAssetSelect,
    onThumbnailClick,
    onAssetDownload,
    onAssetDelete,
    loading,
    readOnly = false,
}) => {
    const theme = useTheme();

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                <CircularProgress />
            </Box>
        );
    }

    const hasFolders = childFolders.length > 0;
    const hasAssets = assets.length > 0;

    if (!hasFolders && !hasAssets) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 300,
                    color: theme.palette.text.secondary,
                }}
            >
                <InsertDriveFileIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
                <Typography variant="h6" color="textSecondary">
                    This folder is empty
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    Add subfolders from the tree or upload assets above
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2.5,
                p: 2,
                justifyContent: { xs: 'center', lg: 'flex-start' },
            }}
        >
            {onFolderOpen &&
                onFolderToggleSelect &&
                childFolders.map((folder) => (
                    <FolderTile
                        key={`folder-${folder._id}`}
                        folder={folder}
                        onOpen={onFolderOpen}
                        isSelected={selectedFolderIds.has(folder._id)}
                        onToggleSelect={onFolderToggleSelect}
                    />
                ))}
            {assets.map((asset) => (
                <AssetTile
                    key={asset._id}
                    asset={asset}
                    isSelected={selectedAssets.has(asset._id)}
                    onSelect={onAssetSelect}
                    onThumbnailClick={onThumbnailClick}
                    onDownload={onAssetDownload}
                    onDelete={onAssetDelete}
                    readOnly={readOnly}
                />
            ))}
        </Box>
    );
};

export default AssetGrid;
